import { useDatabaseContent } from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import {
  getDatabaseFieldOptional,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional
} from '@databases/utils/redux/databases/selectors';

/**
 * Resolves the target field for an ENTRY_DETAILS field by following
 * the reference chain to the target database's lookup field.
 *
 * Returns the full target `DatabaseField` so that callers can access
 * both its `type` and its `settings` (needed when the target is a
 * BACKLINK or PAGE_DETAILS field whose sort category depends on settings).
 *
 * Returns `undefined` if the field is not ENTRY_DETAILS or any step
 * in the resolution chain fails.
 */
export const useEntryDetailsResolvedField = (
  field: DatabaseField | undefined
): DatabaseField | undefined => {
  const { contentId } = useDatabaseContent();

  return defaultStoreUtils.useEqualSelector((state) => {
    if (!field || field.type !== DatabaseFieldType.ENTRY_DETAILS || !contentId) {
      return undefined;
    }

    const database = selectDatabaseByContentIdOptional(state, { contentId });
    if (!database || isBrokenDatabase(database)) {
      return undefined;
    }

    const referencedField = getDatabaseFieldOptional(database, field.settings.fieldId);
    if (
      referencedField?.type !== DatabaseFieldType.ENTRY_LINK &&
      referencedField?.type !== DatabaseFieldType.BACKLINK
    ) {
      return undefined;
    }

    let targetDatabase;
    if (referencedField.settings.databaseId && referencedField.settings.databaseSpace) {
      const dbByIdResult = selectDatabaseByIdOptional(state, {
        spaceKey: referencedField.settings.databaseSpace.key,
        id: referencedField.settings.databaseId
      });
      targetDatabase =
        dbByIdResult &&
        selectDatabaseByContentIdOptional(state, {
          contentId: dbByIdResult.contentId
        });
    } else if (referencedField.settings.contentId) {
      targetDatabase = selectDatabaseByContentIdOptional(state, {
        contentId: referencedField.settings.contentId
      });
    }

    if (!targetDatabase || isBrokenDatabase(targetDatabase)) {
      return undefined;
    }

    const targetField = getDatabaseFieldOptional(
      targetDatabase,
      field.settings.lookupFieldId
    );

    return targetField;
  });
};
